<script setup lang="ts">
import { ref, watch } from 'vue';
import { useLocale } from '@/plugins/translation';
import FilterButton from '@/components/FilterButton.vue';
import { useDisplay } from 'vuetify';
import { DrawerBottom } from '@ilteducation/components';
import { FilterConfig } from '@ilteducation/videos';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
  hasActiveFilters: boolean;
  filters: FilterConfig[];
  initialSearchString: string;
  initialFilterValues: Record<string, any>;
}>();

const searchString = ref(props.initialSearchString);
const { t, l } = useLocale();
const { xs } = useDisplay();
const showFilterDrawer = ref(false);
const localFilters = ref({ ...props.initialFilterValues });

const emit = defineEmits<{
  (e: 'update:search', searchString: string): void;
  (e: 'search', searchString: string): void;
  (e: 'update:filters', newFilters: Record<string, any>): void;
}>();

const onSearch = () => {
  emit('search', searchString.value);
};

const removeAllFilters = () => {
  localFilters.value = {};
  emit('update:filters', localFilters.value);
};

watch(
  () => searchString.value,
  (newVal) => {
    emit('update:search', newVal);
  },
);

watch(
  localFilters,
  (newValues: Record<string, any>) => {
    emit('update:filters', newValues);
  },
  { deep: true },
);
</script>

<template>
  <v-row>
    <v-col cols="10" sm="12" md="3">
      <v-text-field
        v-model="searchString"
        class="search-field no-floating-label"
        @keyup.enter="onSearch"
        :aria-label="t(l.search.search)"
        :label="t(l.search.search)"
        :placeholder="t(l.search.search)"
        maxlength="40"
        density="comfortable"
        persistent-placeholder
        clearable
        hide-details
      >
        <template #prepend-inner>
          <FontAwesomeIcon class="mr-1" icon="fa-regular fa-magnifying-glass" />
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="2" v-if="xs" class="d-flex align-center justify-center pa-0">
      <FilterButton @click="showFilterDrawer = true" :has-active-filters="hasActiveFilters" />
    </v-col>
    <DrawerBottom
      color="surface-container-low"
      v-if="xs"
      :modelValue="showFilterDrawer"
      :height="550"
      @close="showFilterDrawer = false"
    >
      <template #topbar>
        <v-card-title class="d-flex justify-space-between align-center px-0">
          <v-btn
            class="rounded-lg text-none pa-1"
            size="large"
            color="primary"
            variant="text"
            :text="t(l.button_reset)"
            @click="removeAllFilters"
          />
          <v-spacer />
          <p class="text-center flex-1-0">{{ t(l.search.filters) }}</p>
          <v-spacer />
          <v-btn
            class="rounded-lg text-none pa-1"
            size="large"
            color="primary"
            variant="text"
            @click="showFilterDrawer = false"
          >
            {{ t(l.done) }}
          </v-btn>
        </v-card-title>
      </template>
      <v-sheet color="transparent">
        <v-container class="py-2 px-0 mt-8 gr-4 d-flex flex-column">
          <v-select
            v-for="filter in filters"
            :key="filter.name"
            v-model="localFilters[filter.name]"
            :items="filter.items"
            :placeholder="filter.placeholder"
            :aria-label="filter.ariaLabel"
            :item-title="filter.itemTitle"
            :item-value="filter.itemValue"
            hide-details
            single-line
            clearable
            v-bind="filter.extraProps"
          />
        </v-container>
      </v-sheet>
    </DrawerBottom>
    <template v-else>
      <v-col v-for="filter in filters" :key="filter.name" :cols="filter.cols" :sm="filter.sm" :md="filter.md">
        <v-select
          v-model="localFilters[filter.name]"
          :items="filter.items"
          :placeholder="filter.placeholder"
          :label="filter.placeholder"
          :aria-label="filter.ariaLabel"
          :item-title="filter.itemTitle"
          :item-value="filter.itemValue"
          hide-details
          single-line
          clearable
          persistent-placeholder
          v-bind="filter.extraProps"
        />
      </v-col>
    </template>
  </v-row>
</template>

<style scoped lang="scss">
:deep(.no-floating-label .v-field-label),
:deep(.no-floating-label .v-field__outline__notch) {
  display: none !important;
}
</style>
